/**
 * A visitor's first touch, kept in their own browser (ADR-0190 §1): where they
 * first came from and the UTC day, plus whether a return on it was reported.
 * No identifier and no cookie, and never sent automatically: a signed-out boot
 * echoes it to one beacon, and a sign-in start carries it. Every field is the
 * server's — this module only stores and echoes what it was answered.
 */

export interface FirstTouch {
  source: string;
  campaign: string;
  day: string;
  returned: boolean;
}

const KEY = "sageleaf.firstTouch";

export function storedFirstTouch(): FirstTouch | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<FirstTouch>;
    if (typeof v.source !== "string" || typeof v.day !== "string") return null;
    return {
      source: v.source,
      campaign: typeof v.campaign === "string" ? v.campaign : "",
      day: v.day,
      returned: v.returned === true,
    };
  } catch {
    return null;
  }
}

function store(v: FirstTouch | null) {
  try {
    if (v) window.localStorage.setItem(KEY, JSON.stringify(v));
    else window.localStorage.removeItem(KEY);
  } catch {
    // Storage refused (private mode, quota): the visit is still counted.
  }
}

let reported = false;

/**
 * reportFirstTouch is the signed-out boot's beacon, once per page load. The
 * answer replaces what is stored, whatever it is. A failure keeps the old value.
 */
export async function reportFirstTouch(): Promise<void> {
  if (reported) return;
  reported = true;
  try {
    const res = await fetch("/api/v1/traffic/first-touch", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ first_touch: storedFirstTouch() }),
    });
    if (!res.ok) return;
    const body = (await res.json()) as { first_touch: FirstTouch | null };
    store(body.first_touch);
  } catch {
    // Offline or blocked: nothing to count, nothing lost.
  }
}

/** The sign-in start's path; the one place a first touch is carried. */
export const SIGN_IN_START = "/api/v1/auth/google/start";

/** withFirstTouch adds the stored first touch to a sign-in start URL. */
export function withFirstTouch(url: string): string {
  const ft = storedFirstTouch();
  if (!ft || !url.startsWith(SIGN_IN_START)) return url;
  const u = new URL(url, window.location.origin);
  u.searchParams.set("ft_source", ft.source);
  if (ft.campaign) u.searchParams.set("ft_campaign", ft.campaign);
  u.searchParams.set("ft_day", ft.day);
  return u.pathname + u.search;
}

/**
 * carryFirstTouchOnSignIn makes every sign-in link carry the stored value when
 * pressed, not when drawn: a link drawn before the beacon answered would
 * otherwise sign up as `unknown`. Installed once, from the app's boot.
 */
export function carryFirstTouchOnSignIn(doc: Document = document) {
  doc.addEventListener(
    "click",
    (e) => {
      const a = (e.target as Element | null)?.closest?.("a");
      if (!a) return;
      const href = a.getAttribute("href");
      if (href && href.startsWith(SIGN_IN_START)) a.setAttribute("href", withFirstTouch(href));
    },
    true,
  );
}
